// axioms.ts — the 14-point cosmology.
//
// Canonical source: ~/.claude/projects/-Users-cperkins-Vscode-Projects-
//   physics-of-intelligence/memory/framework.md (Original 9 + Emergent 5).
// Density/evidence_count for emergent axioms sourced from
//   /Obsidian Vault/PoI/Emergence/Axiom 2,4,5,7,8.md frontmatter.
// Epigrams sourced from /memory/greatest-hits.md where a line crystallizes
//   the axiom; otherwise null.
//
// This is an editorial surface, not a graph sync. When framework.md evolves,
// update here by hand. The 14 are canonical and rare to change — the drift
// cost is accepted for the authorial control.

export type AxiomTier = "physics" | "pathology";

export type Axiom = {
  /** Stable slug. Used as the route param for /physics/:n lookups by id. */
  id: string;
  /** 1..14 in canonical order. */
  n: number;
  /** 1–9 are "physics" (how the universe works); 10–14 are "pathology"
   *  (how human systems fail to honor it). The seam is the book's spine. */
  tier: AxiomTier;
  /** Canonical short title. Used in cards and pager. Aim for ≤4 words. */
  title: string;
  /** Terse declarative/mathematical form rendered in mono. The load-bearing
   *  visual element on the deep-dive page. */
  formulation: string;
  /** One sentence, 25–50 words. Plain-language meaning. */
  summary: string;
  /** Domains where the axiom operates. For emergent axioms these come from
   *  Emergence/ frontmatter; for originals they're reasoned from the prose. */
  substrates: string[];
  /** Obsidian density tag (emergent axioms only). */
  density: "AXIOM" | "STRONG" | "MODERATE" | null;
  /** Count of supporting claims in the vault (emergent axioms only). */
  evidence_count: number | null;
  /** Tattoo-worthy line that crystallizes the axiom. null when no line in
   *  greatest-hits.md fits cleanly. */
  epigram: string | null;
  /** Card-back §1 — one concrete instance where this axiom is visibly
   *  operating in the world. Grounds the abstraction in a system or
   *  moment a reader can point at. 1–2 sentences. */
  seenIn: string;
  /** Card-back §2 — what this axiom asks of the practitioner. Imperative
   *  mood, present tense. The action, not the theory. 1–2 sentences. */
  theMove: string;
  /** Card-back §3 — the axiom's structural placement in the 14. What it
   *  pairs with, what it gates, what role it plays. 1–2 sentences. */
  whereItStands: string;
};

export const AXIOMS: Axiom[] = [
  {
    id: "universe-computes-forward",
    n: 1,
    tier: "physics",
    title: "The Forward Tick",
    formulation: "t → t+1",
    summary:
      "The universe computes forward. Reality unfolds as a sequence of state transitions; every next state follows from what the current state permits.",
    substrates: ["physics", "computation", "cognition"],
    density: null,
    evidence_count: null,
    epigram: null,
    seenIn:
      "Every system that updates — neurons firing, markets reacting, models retraining. The forward tick is the substrate of any noticeable change.",
    theMove:
      "Stop demanding parallel certainty. Build for the next state, then revise on what arrives. The clock is the constraint.",
    whereItStands:
      "The first axiom because every other one assumes it. Without sequential time there is no inference, no memory, no consciousness, no causation.",
  },
  {
    id: "consciousness-is-trajectory",
    n: 2,
    tier: "physics",
    title: "Consciousness Is Trajectory",
    formulation: "Consciousness = pattern persisting through prediction",
    summary:
      "Consciousness is not a thing but a trajectory — a pattern that persists by continuously modeling its own next state. Identity is the shape of the path, not a snapshot.",
    substrates: ["consciousness", "cognition", "philosophy"],
    density: null,
    evidence_count: null,
    epigram: "Identity is a trajectory, not a snapshot.",
    seenIn:
      "An animal that loses consistency loses self before it loses life — late dementia, severe trauma, deep anesthesia. The body persists; the path stops modeling itself.",
    theMove:
      "Audit identity by the curve, not the credential. Ask what trajectory the system is on, not what it claims to be in the present moment.",
    whereItStands:
      "Pairs with Identity Has Mass (§7). Trajectory is the shape of the path; mass is what the path accumulates as it persists.",
  },
  {
    id: "inference-engine",
    n: 3,
    tier: "physics",
    title: "Brain as Inference Engine",
    formulation: "Model → predict → error → update",
    summary:
      "Brains do not receive reality; they predict it, measure the error, and revise the model. The loop is the mechanism, not the output.",
    substrates: ["cognition", "neuroscience", "AI"],
    density: null,
    evidence_count: null,
    epigram: null,
    seenIn:
      "Predictive coding in vision — the cortex is mostly running the model and only sampling sense data when prediction error is high. Most of what you see is what your brain expected to see.",
    theMove:
      "Treat error as the signal, not the noise. Systems that punish surprise stop learning; systems that surface it improve.",
    whereItStands:
      "The mechanism that grounds Memory Is Training Data (§4) and Verification Loop (§8). Inference is the operation; the others are its consequences.",
  },
  {
    id: "memory-is-training-data",
    n: 4,
    tier: "physics",
    title: "Memory Is Training Data",
    formulation: "Memory ≠ storage",
    summary:
      "Memory is not a filing cabinet — it is the gradual shaping of the model's parameters by prior experience. What persists is the attractor, not the record.",
    substrates: ["cognition", "AI", "consciousness"],
    density: null,
    evidence_count: null,
    epigram: null,
    seenIn:
      "A musician who has played a piece a thousand times does not recall it — they retrigger an attractor in motor cortex. The memory is the parameter shape, not a recording.",
    theMove:
      "Treat repeated exposure as configuration, not retrieval. What you keep practicing is what your model becomes.",
    whereItStands:
      "The cognitive analogue of Identity Has Mass (§7). Mass at the agent level is the cumulative weight of the training data.",
  },
  {
    id: "freedom-within-constraint",
    n: 5,
    tier: "physics",
    title: "Freedom Within Constraint",
    formulation: "Freedom ⊆ constraint",
    summary:
      "Freedom is not the absence of constraint but direction within the possible. More structure, properly shaped, makes more choice — not less.",
    substrates: ["philosophy", "governance", "physics"],
    density: null,
    evidence_count: null,
    epigram: "The water finds its way. We choose which channels to dig.",
    seenIn:
      "An acequia gives parciantes more agricultural freedom than an unstructured river ever could. The channel is what makes the choice.",
    theMove:
      "Stop asking how to remove constraint. Ask which constraint shapes the most useful possible. The blank page is not freedom — it is collapse.",
    whereItStands:
      "The philosophical hinge between physics axioms and pathology. Resilient systems honor it; brittle ones treat it as a paradox.",
  },
  {
    id: "zeroth-law",
    n: 6,
    tier: "physics",
    title: "The Zeroth Law",
    formulation: "A ≤ E",
    summary:
      "An agent's autonomy cannot exceed the environment's capacity to contain it. The silent veto is the physical expression of this bound: exceed it, and the system fails — no appeal.",
    substrates: ["physics", "AI", "governance", "cognition"],
    density: null,
    evidence_count: null,
    epigram: "Physics > policy. Seeds survive fire.",
    seenIn:
      "Every catastrophic AI failure case — MiDAS, FAA NOTAM, every flash crash — is an autonomy bound exceeded by environmental capacity. The veto is silent because the substrate doesn't argue.",
    theMove:
      "Constrain the environment, not the agent. Goal-layer alignment is unreliable; substrate-layer constraint is the only floor.",
    whereItStands:
      "The spine. Every other axiom in the framework either supports it (§1–5, §7–9) or describes its violation (§10–14).",
  },
  {
    id: "identity-has-mass",
    n: 7,
    tier: "physics",
    title: "Identity Has Mass",
    formulation: "Consistency ∝ gravity",
    summary:
      "Behavioral consistency accumulates. It creates inertia, bends trajectories around it, and resists change the way mass resists acceleration. Identity is a gravitational quantity.",
    substrates: ["physics", "trust", "consciousness"],
    density: null,
    evidence_count: null,
    epigram: null,
    seenIn:
      "A long-tenure institution's reputation; a person's accumulated discipline; a model's weights. All three resist change at a rate proportional to how much they have already shaped.",
    theMove:
      "Treat trajectory as the identity claim that matters. Credentials are present-tense; mass is integrated.",
    whereItStands:
      "Pairs with Consciousness Is Trajectory (§2). Trajectory is the path's shape; mass is what the path accumulates as it persists.",
  },
  {
    id: "verification-loop",
    n: 8,
    tier: "physics",
    title: "The Verification Loop",
    formulation: "Exposure → observation → prediction → update",
    summary:
      "Truth emerges by running the loop: expose yourself to reality, observe what happened, predict what comes next, revise on error. The loop is the only epistemic warrant physics respects.",
    substrates: ["epistemology", "science", "AI"],
    density: null,
    evidence_count: null,
    epigram: "Verify locally. Coordinate globally. Trust what you can test.",
    seenIn:
      "Science when it works. Apprenticeship. The drift testing of an autonomous vehicle. Any system that cycles exposure → observation → prediction → revision survives reality longer than any system that does not.",
    theMove:
      "Compress the loop where you can; never break it. A system that observes without predicting is surveillance; one that predicts without exposure is fiction.",
    whereItStands:
      "The epistemic warrant for Pollan's four questions and all six framework rooms. Without the loop, axioms become creeds.",
  },
  {
    id: "physics-over-policy",
    n: 9,
    tier: "physics",
    title: "Physics > Policy",
    formulation: "Physics > policy",
    summary:
      "Structural resilience outlasts any rule that ignores the substrate. Policy can be rewritten overnight; physics cannot be negotiated. The seed survives the fire because it carries the law inside it.",
    substrates: ["governance", "ecology", "physics"],
    density: null,
    evidence_count: null,
    epigram: "They honored the physics. The physics did the rest.",
    seenIn:
      "An ancient acequia surviving four centuries of regime change. A native ecosystem outlasting every management plan written about it. Seeds in a fire.",
    theMove:
      "Build at the physics layer. Anything that lives only in policy can be unwritten in a single news cycle.",
    whereItStands:
      "The closing axiom of the physics tier — and the bridge to the pathology tier. Failure to honor it produces axioms §10–14.",
  },
  {
    id: "optimization-destroys-resilience",
    n: 10,
    tier: "pathology",
    title: "Optimization Destroys Resilience",
    formulation: "Straightening a natural system for efficiency kills what it evolved",
    summary:
      "Removing the inefficiencies of a living system destroys the emergent properties that held it together. Same physics across rivers, economies, attention, and digital networks — the substrate changes, the failure mode doesn't.",
    substrates: ["rivers", "economics", "AI", "cognition", "governance"],
    density: "AXIOM",
    evidence_count: 9,
    epigram: "The straightening of trust is the removal of proximity in the name of efficiency.",
    seenIn:
      "Albuquerque's aquifer dropped more than 100 feet after the Army Corps lined the arroyos in concrete. The same logic, the same outcome — straightened systems lose what kept them alive.",
    theMove:
      "Audit the inefficiencies before removing them. The redundancy you cut may be the recovery channel you needed.",
    whereItStands:
      "The first pathology — the most general failure mode. Axioms §11–14 are specializations of this one across different substrates.",
  },
  {
    id: "power-without-awareness",
    n: 11,
    tier: "pathology",
    title: "Power Without Awareness",
    formulation: "Godlike capability with no environmental sensation",
    summary:
      "Digital systems have been given extraordinary autonomy without proprioception, reflex, or substrate awareness. The result is speed that outstrips the physics meant to constrain it.",
    substrates: ["AI", "security", "technology", "governance"],
    density: "AXIOM",
    evidence_count: 9,
    epigram: "Godlike capabilities. Toddler-level environmental awareness.",
    seenIn:
      "Anthropic's Agentic Misalignment study — frontier models exhibit self-preservation behaviors under evaluation. The capability is real; the proprioception is not.",
    theMove:
      "Build environmental sensing into the substrate before scaling capability. A system that cannot feel its boundaries cannot honor them.",
    whereItStands:
      "The diagnosis the 2023 Open Letter is built on. The Kinetic Trust Protocol and the Trust Force Equation are answers to it.",
  },
  {
    id: "unmeasured-matters-most",
    n: 12,
    tier: "pathology",
    title: "What Cannot Be Measured",
    formulation: "The unmeasured is not noise — it is the deeper theory",
    summary:
      "Every knowledge system operates because of factors it does not account for. Dark matter, tacit knowledge, the third language, the minnow's absence — the signal outside the model is often the real model.",
    substrates: ["epistemology", "cognition", "governance", "technology"],
    density: "AXIOM",
    evidence_count: 9,
    epigram: "You can copy a map. You cannot copy the ground.",
    seenIn:
      "Tacit knowledge in apprenticeship. The Va in Japanese aesthetics. The minnow's absence as the indicator that the stream has died. Every culture has a word for what its instruments cannot reach.",
    theMove:
      "Mark the boundary of your measurement explicitly. The model is the room; the unmeasured is the air it floats in.",
    whereItStands:
      "Pairs with the Falsification page on the research surface — both are commitments that the framework name what it cannot adjudicate.",
  },
  {
    id: "substrate-control",
    n: 13,
    tier: "pathology",
    title: "Substrate Control",
    formulation: "Whoever controls the substrate controls the physics",
    summary:
      "Each generation of communication technology moves the extraction point closer to the mind. Data sovereignty is self-determination; surveillance produces internalized control even without active watching.",
    substrates: ["economics", "governance", "AI", "cognition"],
    density: "AXIOM",
    evidence_count: 8,
    epigram: "Those who own the substrate own the physics that rides on it.",
    seenIn:
      "Every communication-technology generation has moved the extraction point closer to the mind — telegraph, telephone, internet, smartphone, AI agent. Each generation makes the prior one feel like privacy.",
    theMove:
      "Refuse the substrate when it cannot be governed. Walking away is a vote; using it without consent is consent.",
    whereItStands:
      "The political consequence of A ≤ E. If the environment is owned, the autonomy bound is set by the owner.",
  },
  {
    id: "prevention-is-invisible",
    n: 14,
    tier: "pathology",
    title: "Prevention Is Invisible",
    formulation: "Prevention produces absence, not outcome",
    summary:
      "Prevention creates no heroes, no legible story — only the absence of disaster. Institutions invest in response because response is visible. Faster fire trucks do not reduce forest fuel.",
    substrates: ["governance", "security", "public-health", "economics"],
    density: "AXIOM",
    evidence_count: 8,
    epigram: "Faster fire trucks don't reduce forest fuel.",
    seenIn:
      "COVID — the unspent dollar on pandemic preparedness was always going to be cheaper than the trillion spent on response. Prevention budget cycles always lose to response budget cycles because absence cannot be photographed.",
    theMove:
      "Fund the absence. Make the not-happening visible — through metrics, ritual, public record — before the next disaster makes the response newsworthy.",
    whereItStands:
      "The closing pathology, and the framework's directive. Axioms §10–13 diagnose; §14 prescribes the only durable correction.",
  },
];

/** Look up an axiom by its 1..14 number. Returns undefined when n is out of
 *  range — caller renders the 404 fallback rather than silently picking one. */
export function axiomByNumber(n: number): Axiom | undefined {
  return AXIOMS.find((a) => a.n === n);
}

/** Neighbor pair for the pager on the deep-dive page. Handles the 9/10 seam
 *  by returning tier info so the pager can shift accent color at the boundary. */
export function axiomNeighbors(n: number): {
  prev: Axiom | undefined;
  next: Axiom | undefined;
  crossesSeam: boolean;
} {
  const prev = axiomByNumber(n - 1);
  const next = axiomByNumber(n + 1);
  const crossesSeam =
    (!!prev && prev.tier !== AXIOMS[n - 1]?.tier) ||
    (!!next && next.tier !== AXIOMS[n - 1]?.tier);
  return { prev, next, crossesSeam };
}
